import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';

const Admins = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditEmail, setShowEditEmail] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', email: '' });
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/auth/users');
      setUsers(response.data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.error || 'שגיאה בטעינת משתמשים');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await axios.post('/auth/users', newUser);
      setSuccess(response.data.message || 'המשתמש נוצר בהצלחה');
      setNewUser({ username: '', email: '' });
      setShowAddForm(false);
      // Refresh users list
      await fetchUsers();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'שגיאה ביצירת משתמש';
      const errorDetails = err.response?.data?.details || '';
      setError(errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage);
      console.error('Create user error:', err.response?.data);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק משתמש זה?')) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await axios.delete(`/auth/users/${userId}`);
      setSuccess(response.data.message || 'המשתמש נמחק בהצלחה');
      // Refresh users list
      await fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה במחיקת משתמש');
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await axios.put('/auth/profile', { email: newEmail });
      setSuccess(response.data.message);
      setShowEditEmail(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בעדכון אימייל');
    }
  };

    return (
    <Layout>
      {loading ? (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">טוען...</div>
      </div>
      ) : (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">ניהול מנהלים</h1>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
        >
          {showAddForm ? 'ביטול' : '+ הוסף מנהל'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Add User Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">הוספת מנהל חדש</h2>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                שם משתמש
              </label>
              <input
                type="text"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-nexolog-teal focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                אימייל
              </label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-nexolog-teal focus:border-transparent"
                required
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              💡 המשתמש יקבל אימייל עם קישור להגדרת סיסמה
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="bg-nexolog-teal hover:bg-nexolog-teal/90 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                צור משתמש
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-lg font-medium transition-colors"
              >
                ביטול
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Current User Email Edit */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">הפרופיל שלי</h2>
        <div className="space-y-3">
          <div>
            <span className="text-sm font-medium text-gray-700">שם משתמש: </span>
            <span className="text-gray-900">{user?.username}</span>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-700">אימייל: </span>
            <span className="text-gray-900">{user?.email}</span>
          </div>
          {!showEditEmail ? (
            <button
              onClick={() => {
                setShowEditEmail(true);
                setNewEmail(user?.email || '');
              }}
              className="text-nexolog-teal hover:underline text-sm font-medium"
            >
              ערוך אימייל
            </button>
          ) : (
            <form onSubmit={handleUpdateEmail} className="space-y-3">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-nexolog-teal focus:border-transparent"
                required
              />
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="bg-nexolog-teal hover:bg-nexolog-teal/90 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  שמור
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditEmail(false)}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  ביטול
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">רשימת מנהלים ({users.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  שם משתמש
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  אימייל
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  תאריך הצטרפות
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  פעולות
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">{u.username}</span>
                      {u.id === user?.id && (
                        <span className="mr-2 px-2 py-1 text-xs font-semibold rounded-full bg-nexolog-teal text-white">
                          אתה
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {u.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {new Date(u.created_at).toLocaleDateString('he-IL')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {u.id !== user?.id && (
                      <button
                        onClick={() => handleDeleteUser(u.id)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        מחק
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
      )}
    </Layout>
  );
};

export default Admins;

